import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ImageIcon, ArrowRight } from "lucide-react";
import { formatPrice } from "@/store/storeCart";

type DemoProduct = {
  id: string;
  slug: string;
  title: string;
  base_price_cents: number;
  currency: string;
  images: string[] | null;
  vendor_name: string | null;
};

const StoreDemoPreview = () => {
  const [products, setProducts] = useState<DemoProduct[]>([]);

  useEffect(() => {
    supabase
      .from("store_products")
      .select("id, slug, title, base_price_cents, currency, images, vendor:store_vendors_public!vendor_id(display_name)")
      .eq("status", "active")
      .limit(8)
      .then(({ data }) => {
        if (!data) return;
        setProducts(
          data.map((p: any) => ({
            id: p.id,
            slug: p.slug,
            title: p.title,
            base_price_cents: p.base_price_cents,
            currency: p.currency,
            images: p.images,
            vendor_name: (p as any).vendor?.display_name ?? null,
          }))
        );
      });
  }, []);

  if (products.length === 0) return null;

  return (
    <section className="border-t border-border bg-secondary/10">
      <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Wavlynq Store
              </p>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-amber-500/50 text-amber-500">
                Demo
              </Badge>
            </div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
              Shop the community.
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed max-w-xl">
              Curated goods from vendors across all nine communities — apparel,
              wellness, digital content, art, and more.
            </p>
          </div>
          <Link
            to="/store"
            className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline whitespace-nowrap"
          >
            Browse full store <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {products.map((p) => {
            const img = Array.isArray(p.images) ? p.images[0] : null;
            return (
              <Link to={`/store/product/${p.slug}`} key={p.id} className="group">
                <Card className="overflow-hidden transition-all hover:shadow-lg relative">
                  {/* Demo badge */}
                  <div className="absolute top-2 right-2 z-10">
                    <Badge className="bg-amber-500/90 text-white text-[10px] uppercase tracking-wider hover:bg-amber-500">
                      Demo
                    </Badge>
                  </div>
                  <div className="aspect-square bg-muted/50 flex items-center justify-center overflow-hidden">
                    {img ? (
                      <img
                        src={img}
                        alt={p.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-muted-foreground/40" />
                    )}
                  </div>
                  <div className="p-3 space-y-0.5">
                    {p.vendor_name && (
                      <p className="text-xs text-muted-foreground truncate">
                        {p.vendor_name}
                      </p>
                    )}
                    <h3 className="text-sm font-medium leading-tight line-clamp-2">
                      {p.title}
                    </h3>
                    <p className="text-sm font-semibold pt-0.5">
                      {formatPrice(p.base_price_cents, p.currency)}
                    </p>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="mt-6 text-center md:hidden">
          <Link
            to="/store"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            Browse full store <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default StoreDemoPreview;
